/* ==========================================================================
   Poster Kingz — Mobile PDP reorder (Phase 5i)
   On screens < 750px, physically reorder the direct children of
   .product-page__content-sidebar so that customisation + Add-to-Cart surface
   above supporting content. Replaces the fragile CSS :has() approach from
   Phase 5h — DOM reorder is specificity-proof and works regardless of
   Customily's late injection.

   Order on mobile:
     1. Title (+ information badges)
     2. Price (with the MADE-IN-UK eyebrow)
     3. Variant / size picker
     4. Buy-buttons block  ← Customily inputs inject INSIDE this block,
                             so pulling it up hoists Customily with it.
     5. pk-payment-badges (wallet + BNPL trust row)
     6. Pay-in-3 (payment_terms)
     7. Text / icon trust row
     8. Shipping policy line
     9. Free-delivery-bar + inventory bar
     10. Pickup availability
     20. Details / accordions (size guide, FAQ, reviews)
     25. Share row
     50. Anything unclassified (description, reviews, footer button)

   The script is idempotent: rerunning it on the same DOM is a no-op.
   A MutationObserver re-runs once for ~15s after load to catch Customily
   injection. Desktop viewports are left untouched.
   ========================================================================== */
(function () {
  'use strict';

  var MQ = '(max-width: 749px)';
  var SIDEBAR_SEL = '.product-page__content-sidebar';

  function isMobile() {
    return window.matchMedia && window.matchMedia(MQ).matches;
  }

  /**
   * Return a numeric priority for a direct child of the sidebar.
   * Lower = higher on the page.
   */
  function priority(el) {
    if (!el || el.nodeType !== 1) return 60;

    // 2. Title — confirms what they're customising
    if (el.querySelector && el.querySelector('.product__title')) return 2;

    // 3. Inventory bar ("Just X left. Order soon!") — urgency directly under title
    if (el.id && el.id.indexOf('inventory-') === 0) return 3;
    if (el.querySelector && el.querySelector('[class*="inventory"]')) return 3;

    // 4. Free-delivery bar ("FREE SHIPPING") — trust/reassurance next
    if (el.id === 'product-page-free-delivery') return 4;
    if (el.querySelector && el.querySelector('free-delivery-bar, .free-delivery-bar')) return 4;

    // 5. Price (MADE-IN-UK eyebrow renders beneath via CSS ::after)
    if (el.querySelector && el.querySelector('.product__price')) return 5;

    // 6. Variant selectors (size, paper, frame)
    if (el.querySelector && el.querySelector('variant-radios, variant-selects, fieldset.product-form__input')) return 6;

    // 7. Buy-buttons block. Most reliable signal: a <product-form> tag.
    //    Customily injects its inputs INSIDE this block, so hoisting buy_buttons
    //    automatically hoists Customily above the trust row / shipping / accordions.
    if (el.querySelector && el.querySelector('product-form, .product-form__buttons')) {
      return 7;
    }

    // 8. Our wallet + BNPL badge row (standalone block).
    if (el.classList && el.classList.contains('pk-payment-badges')) return 8;
    if (el.querySelector && el.querySelector('.pk-payment-badges')) return 8;

    // 9. Pay-in-3
    if (el.classList && el.classList.contains('payment_terms')) return 9;
    if (el.querySelector && el.querySelector('.payment_terms, shopify-payment-terms')) return 9;

    // 10. Icon trust row (Gallery quality / Tracked delivery / Made in UK icons)
    if (el.querySelector && el.querySelector('.product__text__container, [class*="text-with-icons"]')) return 10;

    // 11. Shipping line
    if (el.querySelector && el.querySelector('.shipping_policy')) return 11;

    // 12. Pickup availability
    if (el.tagName && el.tagName.toLowerCase() === 'pickup-availability') return 12;
    if (el.querySelector && el.querySelector('pickup-availability')) return 12;

    // 20. Accordions / details
    if (el.querySelector && el.querySelector('details.chm-toggle, details')) return 20;

    // 25. Share / popup / simple-link row
    if (el.classList && el.classList.contains('product-links__container')) return 25;
    if (el.querySelector && el.querySelector('[class*="share"], .product__simple-link')) return 25;

    return 50;
  }

  function reorder() {
    var sidebar = document.querySelector(SIDEBAR_SEL);
    if (!sidebar) return;

    if (!isMobile()) {
      // Desktop — nothing to do. Source order is already fine.
      sidebar.removeAttribute('data-pk-reordered');
      return;
    }

    var children = Array.prototype.slice.call(sidebar.children);
    if (children.length === 0) return;

    // Compute priorities and keep original index for stable sort on ties
    var pairs = children.map(function (c, i) {
      return { el: c, p: priority(c), i: i };
    });

    // Check if already in correct order — early exit = true no-op, no reflow
    var alreadyOrdered = true;
    for (var j = 1; j < pairs.length; j++) {
      if (pairs[j].p < pairs[j - 1].p) { alreadyOrdered = false; break; }
    }
    if (alreadyOrdered) {
      sidebar.setAttribute('data-pk-reordered', 'stable');
      return;
    }

    pairs.sort(function (a, b) {
      if (a.p !== b.p) return a.p - b.p;
      return a.i - b.i;
    });

    // Batch into a fragment to minimise reflow
    var frag = document.createDocumentFragment();
    pairs.forEach(function (p) { frag.appendChild(p.el); });
    sidebar.appendChild(frag);
    sidebar.setAttribute('data-pk-reordered', 'mobile');
  }

  // Run once on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reorder);
  } else {
    reorder();
  }

  // Rerun on viewport changes (mobile <-> desktop)
  if (window.matchMedia) {
    try {
      window.matchMedia(MQ).addEventListener('change', reorder);
    } catch (e) {
      // Safari <14 fallback
      window.addEventListener('resize', reorder);
    }
  }

  // Watch for Customily / async injections. When an element is added or
  // removed inside the sidebar, re-classify and reorder. Throttled via rAF.
  var rafPending = false;
  function scheduleReorder() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      reorder();
    });
  }

  function attachObserver() {
    var sidebar = document.querySelector(SIDEBAR_SEL);
    if (!sidebar || !window.MutationObserver) return;
    var obs = new MutationObserver(scheduleReorder);
    obs.observe(sidebar, { childList: true, subtree: true });
    // Disconnect after 20s — Customily injects within a few seconds; no point
    // observing forever.
    setTimeout(function () { obs.disconnect(); }, 20000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachObserver);
  } else {
    attachObserver();
  }
})();
